/**
 * GUPM-302 — aviso de nova versão. Banner persistente no canto inferior esquerdo.
 *
 * Requisito do dono do produto: fica FIXO ali, em qualquer tela do app, e continua no
 * lugar ao rolar a página.
 *
 * ## Posicionamento — as três decisões que têm motivo
 *
 * 1. **`position`/`left`/`zIndex` em estilo inline**, não em classe: assim o contrato de
 *    posicionamento é assertável por `getComputedStyle` no jsdom. O resto do visual é
 *    Tailwind.
 * 2. **`z-index: 40`** (`Z_INDEX_AVISO`) — abaixo de TODO overlay do app, para nunca
 *    cobrir botão de ação de um modal aberto; reaparece quando o modal fecha. Estava
 *    em 90, o que só o deixava abaixo dos modais `z-[100]`: medido no navegador em
 *    31/08 (375×812), o aviso tapava os botões "Sim"/"Não" da etapa de DECISÃO do
 *    protocolo dentro do `AbrirTarpModal` (`z-[60]`). Ver `Z_INDEX_AVISO` e o guard.
 * 3. **`bottom-20 md:bottom-4`** — no celular a nav é `fixed bottom-0 left-0 right-0`
 *    (só `md:hidden`), então rente ao rodapé o aviso cobriria a NAVEGAÇÃO. A folga
 *    vertical o tira de cima dela; no desktop, onde não há nav inferior, fica rente.
 *
 * ## Nunca recarrega sozinho
 *
 * Um operador no meio de uma TARP perderia a tela de vista. O componente só oferece.
 *
 * ## Não há como dispensar
 *
 * O aviso incomoda de propósito — é o que faz a pessoa recarregar. Sai da tela só
 * quando a aba recarrega de fato (pelo botão ou por um F5); não existe estado interno
 * que o esconda, nem `localStorage`.
 *
 * A aba obsoleta não deixa de ser obsoleta porque a pessoa fechou o aviso: escondê-lo
 * deixaria alguém rodando código velho sem nenhum lembrete. E incomodar sai barato aqui
 * — o aviso não bloqueia nada, fica no canto, e a pessoa recarrega quando quiser.
 *
 * ## O texto não promete preservar o que está na tela
 *
 * A versão anterior dizia "Nada do que você registrou se perde". Vale para o rascunho de
 * abertura de TARP (autosave, GUPM-178) e para mais nada: `NovoPlanoModal` tem 14
 * `useState` sem persistência e o checklist do `ShiftHandoverModal` vive em `useState` —
 * nos dois, recarregar perde tudo. Promessa falsa custa o próprio canal: quem perde um
 * plano de ação uma vez passa a ignorar o aviso. Ver `aviso-nao-promete-preservar-o-que-esta-na-tela`.
 *
 * O título e o rótulo do botão vêm de `versao-textos.ts` porque o `ChunkErrorBoundary`
 * anuncia o mesmo assunto e precisa dizer o mesmo nome — ver `TITULO_NOVA_VERSAO`.
 */
import { useNovaVersao } from '@/hooks/useNovaVersao';
import { ROTULO_RECARREGAR, TITULO_NOVA_VERSAO } from '@/components/versao-textos';

/**
 * Camada do aviso: ABAIXO de todo overlay do app. O menor hoje é `z-50`
 * (`ShiftHandoverModal`, `EncerrarPlantaoModal`); o mais baixo depois dele é o `z-[60]`
 * do `Modal` da TARP. Exportada para o guard `aviso-fica-abaixo-de-qualquer-modal`,
 * que varre o `src/` e quebra se algum overlay descer até aqui.
 *
 * Ficar abaixo da nav mobile (`z-50`) não esconde o aviso: `bottom-20` (80px) o deixa
 * acima dos 56px da nav — medido, sem colisão. E o mapa Leaflet não o cobre, porque
 * `.leaflet-shell` isola os panes internos (`isolation: isolate`, GUPM-229) — conferido
 * na tela do M2 com este z-index.
 */
export const Z_INDEX_AVISO = 40;

export function AvisoNovaVersao({
  temNova,
  onRecarregar,
}: {
  temNova: boolean;
  onRecarregar: () => void;
}): JSX.Element | null {
  if (!temNova) return null;

  return (
    <div
      role="status"
      // Nomeia o ASSUNTO da região, não repete o texto dela — mesma convenção do
      // `SemPlantaoBanner` (`label="Plantão"`). Sem nome, o leitor de tela anuncia a
      // região `status` sem dizer do que ela trata, e este aviso conviveria com o
      // "Operação sem plantonista" no MESMO `AppShell` como duas regiões
      // indistinguíveis. A razão está escrita em `src/ui/Banner.tsx`, na prop `label`.
      aria-label="Versão do app"
      aria-live="polite"
      style={{ position: 'fixed', left: '1rem', zIndex: Z_INDEX_AVISO }}
      className="bottom-20 md:bottom-4 max-w-[calc(100vw-2rem)] sm:max-w-xs rounded-xl border border-jf-border bg-jf-surface p-3 shadow-lg"
    >
      <p className="text-xs font-bold text-white">{TITULO_NOVA_VERSAO}</p>
      <p className="mt-0.5 text-[11px] leading-snug text-jf-muted">
        Recarregue a página para ver a nova versão.
      </p>
      <div className="mt-2">
        <button
          type="button"
          onClick={onRecarregar}
          className="rounded-lg bg-jf-yellow px-2.5 py-1 text-[11px] font-black text-jf-bg transition hover:brightness-110"
        >
          {ROTULO_RECARREGAR}
        </button>
      </div>
    </div>
  );
}

/** Liga o aviso ao hook de detecção. É o que o `AppShell` monta. */
export function AvisoNovaVersaoConectado(): JSX.Element | null {
  const { temNova, recarregar } = useNovaVersao();
  return <AvisoNovaVersao temNova={temNova} onRecarregar={recarregar} />;
}
